// ./src/components/features/Dashboard/DashboardExpiringDocuments.tsx
// Dashboard component showing expired and expiring soon glovebox documents.
// Refactored to use DashboardSection + DashboardItemTile reusable components.

import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "@/utils/dateHelpers";
import { getCarDisplayName } from "@/utils/carHelpers";
import { buildFullName } from "@/utils/nameHelpers";
import { getDocTypeIcon } from "@/utils/gloveboxHelpers";

import { GloveboxDocument } from "@/utils/api/GloveboxDocumentService";

import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";
import { useDashboardContext } from "@/contexts/DashboardContext";

import { DashboardSection, CountBadge } from "./DashboardSection";
import {
  DashboardItemTile,
  TileBadge,
  TileAction,
  TileStatusType,
} from "./DashboardItemTile";

// ============================================
// Types
// ============================================

export interface DashboardExpiringDocumentsProps {
  /** List of documents (should be filtered to expired/expiring soon via API) */
  documents: GloveboxDocument[];

  /** Whether data is loading */
  loading?: boolean;

  /** Callback when "Update" button is clicked on a document */
  onUpdateDocument?: (document: GloveboxDocument) => void;

  /** Callback when "View All" button is clicked */
  onViewAll?: () => void;

  /** Maximum items to show before "Show More" button (default: 5) */
  initialDisplayCount?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_INITIAL_DISPLAY_COUNT = 5;

// ============================================
// Main Component
// ============================================

export const DashboardExpiringDocuments: React.FC<
  DashboardExpiringDocumentsProps
> = ({
  documents,
  loading = false,
  onUpdateDocument,
  onViewAll,
  initialDisplayCount = DEFAULT_INITIAL_DISPLAY_COUNT,
}) => {
    const { t } = useTranslation(["dashboard", "glovebox", "common"]);
    const { getGloveboxDocTypeById } = useAppEssentialsContext();
    const { getCarById } = useDashboardContext();

    // Count by status for header badges
    const countBadges = useMemo<CountBadge[]>(() => {
      const counts = { expired: 0, expiringSoon: 0 };
      documents.forEach((doc) => {
        if (doc.isExpired) {
          counts.expired++;
        } else {
          counts.expiringSoon++;
        }
      });

      const badges: CountBadge[] = [];
      if (counts.expired > 0) badges.push({ type: "error", count: counts.expired });
      if (counts.expiringSoon > 0) badges.push({ type: "warning", count: counts.expiringSoon });
      return badges;
    }, [documents]);

    // Get document display name
    const getDocumentName = useCallback(
      (document: GloveboxDocument): string => {
        const docType =
          document.docType || getGloveboxDocTypeById(document.docTypeId);
        return (
          document.customTypeName ||
          docType?.name ||
          t("glovebox:card.untitled")
        );
      },
      [getGloveboxDocTypeById, t]
    );

    // Get icon info based on document type
    const getIconInfo = useCallback(
      (document: GloveboxDocument): { iconName: string; iconColor: string } => {
        const docType =
          document.docType || getGloveboxDocTypeById(document.docTypeId);
        const iconName = getDocTypeIcon(docType?.code, docType?.category);
        const iconColor = document.isExpired ? "red" : "orange";
        return { iconName, iconColor };
      },
      [getGloveboxDocTypeById]
    );

    // Build subtitle: "Vehicle/Driver · Expiration Date"
    const getSubtitle = useCallback(
      (document: GloveboxDocument): string => {
        const parts: string[] = [];

        // Vehicle or driver name
        const user = document.user;
        const vehicle =
          document.car || (document.carId ? getCarById(document.carId) : null);

        if (user) {
          const fullName = buildFullName(user);
          parts.push(fullName || user.email || t("common:users.unknownUser"));
        } else if (vehicle) {
          parts.push(getCarDisplayName(vehicle));
        }

        // Expiration date
        if (document.expiresAt) {
          parts.push(formatDate(document.expiresAt));
        }

        return parts.join(" · ");
      },
      [getCarById, t]
    );

    // Build status text
    const getStatusText = useCallback(
      (document: GloveboxDocument): string => {
        if (document.isExpired) {
          const daysAgo = document.daysUntilExpiration
            ? Math.abs(document.daysUntilExpiration)
            : 0;
          return t("common:dashboard.expiringDocuments.expiredDaysAgo", {
            count: daysAgo,
          });
        }

        const daysRemaining = document.daysUntilExpiration ?? 0;
        return t("common:dashboard.expiringDocuments.expiresInDays", {
          count: daysRemaining,
        });
      },
      [t]
    );

    // Get status type based on expiration
    const getStatusType = useCallback(
      (document: GloveboxDocument): TileStatusType => {
        return document.isExpired ? "error" : "warning";
      },
      []
    );

    // Build badges for a document
    const getBadges = useCallback(
      (document: GloveboxDocument): TileBadge[] => {
        return [{ type: document.isExpired ? "expired" : "expiring_soon" }];
      },
      []
    );

    // Build actions for a document
    const getActions = useCallback(
      (document: GloveboxDocument): TileAction[] => {
        if (!onUpdateDocument) return [];

        return [
          {
            type: "view" as const,
            onClick: () => onUpdateDocument(document),
            tooltip: t("common:dashboard.expiringDocuments.updateDocument"),
          },
        ];
      },
      [onUpdateDocument, t]
    );

    // Don't render if no documents and not loading
    if (!loading && documents.length === 0) {
      return null;
    }

    return (
      <DashboardSection
        title={t("common:dashboard.expiringDocuments.title")}
        countBadges={countBadges}
        headerAction={
          onViewAll
            ? {
              label: t("common:dashboard.expiringDocuments.viewAll"),
              onClick: onViewAll,
            }
            : undefined
        }
        loading={loading}
        totalItems={documents.length}
        initialDisplayCount={initialDisplayCount}
      >
        {documents.map((doc, index) => {
          const { iconName, iconColor } = getIconInfo(doc);
          return (
            <DashboardItemTile
              key={doc.id || `doc-${index}`}
              title={getDocumentName(doc)}
              iconName={iconName}
              iconColor={iconColor}
              badges={getBadges(doc)}
              subtitle={getSubtitle(doc)}
              statusText={getStatusText(doc)}
              statusType={getStatusType(doc)}
              actions={getActions(doc)}
            />
          );
        })}
      </DashboardSection>
    );
  };